import React, { useEffect, useState } from 'react';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { Button } from '../../../components/UI/Button';
import { useToast } from '../../../components/UI/ToastProvider';
import { AGENT_RUNTIME_IDS } from '../../../constants/agentPlatform';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type { AutomationInstanceDto } from '../../../types/agentPlatform';
import { apTheme } from '../../../utils/agentPlatformTheme';

const emptyForm = () => ({
  code: '',
  name: '',
  description: '',
  baseUrl: '',
  adminToken: '',
  deploymentMode: 'Private' as 'SaaS' | 'Private',
  connectionType: 'Vpn' as 'PublicApi' | 'Vpn' | 'PrivateLink',
  isDefault: false,
});

export const AutomationInstancesPage: React.FC = () => {
  const { addToast } = useToast();
  const [instances, setInstances] = useState<AutomationInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = () => {
    setLoading(true);
    agentPlatformService
      .getAutomationInstances()
      .then(setInstances)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await agentPlatformService.createAutomationInstance({
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        agentRuntimeId: AGENT_RUNTIME_IDS.nodeRedAutomation,
        baseUrl: form.baseUrl.trim(),
        adminToken: form.adminToken.trim() || undefined,
        deploymentMode: form.deploymentMode,
        connectionType: form.connectionType,
        isDefault: form.isDefault,
      });
      addToast('Instância registrada.', 'success');
      setShowForm(false);
      setForm(emptyForm());
      load();
    } catch {
      addToast('Erro ao registrar instância.', 'error');
    }
  };

  return (
    <AgentPlatformFeatureGate feature="flows">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
        <AgentPlatformPageHeader
          title="Instâncias de Automação"
          subtitle="Registre executores SaaS ou dedicados e vincule-os aos fluxos de trabalho."
          area="workflow"
          actions={
            <Button onClick={() => setShowForm(s => !s)}>
              {showForm ? 'Fechar formulário' : 'Nova instância'}
            </Button>
          }
        />

        {showForm && (
          <form
            onSubmit={submit}
            className={`mb-6 ${apTheme.formPanel} sm:grid-cols-2 max-w-3xl`}
          >
            <input
              className={apTheme.input}
              placeholder="Código"
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              required
            />
            <input
              className={apTheme.input}
              placeholder="Nome"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
            />
            <input
              className={`${apTheme.input} sm:col-span-2`}
              placeholder="URL base (ex: https://automacao.suaempresa.com)"
              value={form.baseUrl}
              onChange={e => setForm(f => ({ ...f, baseUrl: e.target.value }))}
              required
            />
            <input
              className={`${apTheme.input} sm:col-span-2`}
              placeholder="Admin token (opcional)"
              type="password"
              value={form.adminToken}
              onChange={e => setForm(f => ({ ...f, adminToken: e.target.value }))}
            />
            <select
              className={apTheme.input}
              value={form.deploymentMode}
              onChange={e =>
                setForm(f => ({ ...f, deploymentMode: e.target.value as typeof form.deploymentMode }))
              }
            >
              <option value="Private">Private</option>
              <option value="SaaS">SaaS</option>
            </select>
            <select
              className={apTheme.input}
              value={form.connectionType}
              onChange={e =>
                setForm(f => ({
                  ...f,
                  connectionType: e.target.value as typeof form.connectionType,
                }))
              }
            >
              <option value="Vpn">VPN</option>
              <option value="PublicApi">API pública</option>
              <option value="PrivateLink">Private Link</option>
            </select>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
              />
              Instância padrão do tenant
            </label>
            <div className="sm:col-span-2">
              <Button type="submit">Registrar</Button>
            </div>
          </form>
        )}

        <div className={apTheme.tableWrap}>
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead>
              <tr className={apTheme.tableHead}>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">URL</th>
                <th className="px-4 py-3 text-left">Modo</th>
                <th className="px-4 py-3 text-left">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    Carregando…
                  </td>
                </tr>
              )}
              {instances.map(inst => (
                <tr key={inst.id} className="hover:bg-red-50/40 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{inst.name}</div>
                    <div className="text-xs font-mono text-slate-500">{inst.code}</div>
                    {inst.isDefault && (
                      <span className="text-xs text-red-700 font-medium">padrão</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={inst.baseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-700 hover:underline break-all"
                    >
                      {inst.baseUrl}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {inst.deploymentMode} / {inst.connectionType}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={inst.isActive ? 'Active' : 'Paused'}
                      label={inst.isActive ? 'Ativa' : 'Inativa'}
                    />
                    {inst.hasAdminToken && (
                      <span className="ml-1 text-xs text-gray-500">token ✓</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
